import { useMutation } from "react-query";

import { useGlobalToast } from "components/GlobalToastProvider";
import { updateTeamUser } from "api/team";
/** @typedef {import("react-query").UseMutationOptions} UseMutationOptions */

/**
 * The useUpdateTeamUser function is a custom hook that handles updating team member data with error
 * handling and toast notifications.
 * @category Hooks
 * @param {UseMutationOptions} options It accepts an `options` object as a parameter, which can include custom configurations for the
 * mutation operation.
 * @return The `useUpdateTeamUser` function returns an object with the following properties:
 * - `updateTeamUserMutation`: Synchronous mutation function (alias for `mutate`).
 * - Other properties returned by the `useMutation` hook, such as `mutateAsync`, `isLoading`, `isError`, `data`, etc.
 */
export const useUpdateTeamUser = (options = {}) => {
  const { onError = () => {}, ...otherOptions } = options;
  const { showToast } = useGlobalToast();

  const { mutate, ...rest } = useMutation((params) => updateTeamUser(params), {
    onError: (_, variables, context) => {
      showToast({
        message: "Whoops! Something went wrong",
        errorState: true,
        retryHandler: () => mutate(variables, context),
      });
      onError();
    },
    ...otherOptions,
  });

  return {
    updateTeamUserMutation: mutate,
    ...rest,
  };
};
